import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Input, Card, MotifBackground, Badge } from '@/kindra';
import { LogOut, BarChart3, Inbox, Calendar, Calculator as CalcIcon, MessageSquare, FileText, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'kindra-admin-token';
const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};
const auth = () => ({ headers: { Authorization: `Bearer ${tokenStore.get()}` } });

// ============================================================================
// /admin/login
// ============================================================================
export const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const nav = useNavigate();

  // Deep-link preservation — if the user was bounced here from
  // /admin/os/blueprint/<id> (or any admin route), send them straight there
  // after successful login. Guarded to same-origin paths only so a hostile
  // ?returnTo can't be used as an open redirect.
  const returnTo = useMemo(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('returnTo') || '';
      const decoded = decodeURIComponent(raw);
      if (decoded.startsWith('/admin') && !decoded.startsWith('//')) return decoded;
    } catch (_e) { /* noop */ }
    return '';
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/admin/auth/login`, { email, password: pw }, { withCredentials: true });
      if (r.data?.token) {
        tokenStore.set(r.data.token);
        if (r.data.refresh_token) localStorage.setItem('kindra-admin-rt', r.data.refresh_token);
        if (r.data.force_change) {
          nav('/admin/change-password');
        } else if (returnTo) {
          nav(returnTo);
        } else {
          nav('/admin');
        }
      }
    } catch {
      setErr('That email + password combination didn\'t work.');
    } finally { setBusy(false); }
  };

  // Persistent sessions — if a refresh token survives (cookie or PWA storage),
  // sign MJ straight back in without showing the form.
  useEffect(() => {
    let dead = false;
    (async () => {
      if (!tokenStore.get()) {
        try {
          const { trySilentRefresh } = await import('../v2/authRefresh');
          if (await trySilentRefresh() && !dead) { nav(returnTo || '/admin'); return; }
        } catch (_e) { /* fall through to the form */ }
      }
      if (!dead) setRestoring(false);
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (tokenStore.get()) return <Navigate to={returnTo || '/admin'} replace />;
  if (restoring) {
    return (
      <section className="relative bg-navy text-icy min-h-screen flex items-center justify-center">
        <MotifBackground surface="hero" />
        <p data-testid="admin-login-restoring" className="relative k-small text-icy-muted">Signing you back in…</p>
      </section>
    );
  }

  return (
    <section className="relative bg-navy text-icy overflow-hidden min-h-screen pt-28 pb-20">
      <MotifBackground surface="hero" />
      <div className="relative k-container max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <img src="/brand/traveltrucks-logo-reverse.svg" alt="TravelTrucks" className="h-10 w-auto" />
        </div>
        <p className="k-eyebrow text-icy-muted mb-2">Admin</p>
        <h1 className="k-h1 text-icy">TravelTrucks <span className="k-spectrum-text">OS</span></h1>
        <Card className="mt-7 p-6 md:p-8">
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
            <Input label="Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="admin-login-password" />
            {err && <p className="k-small text-sev-high">{err}</p>}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy} data-testid="admin-login-submit">{busy ? 'Signing in…' : 'Sign in'}</Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

// ============================================================================
// /admin/change-password — forced on first login
// ============================================================================
export const AdminChangePasswordPage = () => {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/admin/auth/change-password`, { old_password: oldPw, new_password: newPw }, auth());
      nav('/admin');
    } catch (err2) {
      const msg = err2.response?.data?.detail === 'invalid_credentials' ? "Current password didn't match." : "Couldn't update — try a longer password (10+ characters).";
      setErr(msg);
    } finally { setBusy(false); }
  };
  if (!tokenStore.get()) return <Navigate to="/admin/login" replace />;
  return (
    <section className="relative bg-navy text-icy overflow-hidden min-h-screen pt-28 pb-20">
      <MotifBackground surface="hero" />
      <div className="relative k-container max-w-md">
        <p className="k-eyebrow text-icy-muted mb-2">First sign-in</p>
        <h1 className="k-h1 text-icy">Change your <span className="k-spectrum-text">password</span>.</h1>
        <Card className="mt-7 p-6 md:p-8">
          <form onSubmit={submit} className="space-y-4">
            <Input label="Current password" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} required />
            <Input label="New password (10+ characters)" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={10} />
            {err && <p className="k-small text-sev-high">{err}</p>}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>Set new password</Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

// ============================================================================
// /admin — dashboard shell
// ============================================================================
export const AdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('bookings');
  const [err, setErr] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    if (!tokenStore.get()) { nav('/admin/login'); return; }
    axios.get(`${API}/admin/dashboard`, auth())
      .then(r => setData(r.data))
      .catch(() => { tokenStore.clear(); nav('/admin/login'); });
  }, [nav]);

  const logout = () => { tokenStore.clear(); nav('/admin/login'); };

  if (!data) {
    return <div className="bg-[var(--bg)] min-h-screen pt-28 k-container k-body text-[var(--muted)]">Loading…</div>;
  }

  const TABS = [
    { key: 'bookings', label: 'Bookings', icon: <Calendar size={16} />, count: data.counts.bookings, rows: data.bookings, cols: ['booking_id', 'email', 'full_name', 'status', 'created_at'] },
    { key: 'intakes', label: 'Intakes', icon: <FileText size={16} />, count: data.counts.intakes, rows: data.intakes, cols: ['booking_id', 'email', 'business_type', 'turnover_range', 'received_at'] },
    { key: 'calculator', label: 'Calculator', icon: <CalcIcon size={16} />, count: data.counts.calculator_leads, rows: data.calculator, cols: ['first_name', 'email', 'admin_hours', 'value_per_hour', 'true_cost', 'received_at'] },
    { key: 'contact', label: 'Contact', icon: <MessageSquare size={16} />, count: data.counts.contact_leads, rows: data.contact, cols: ['name', 'email', 'received_at'] },
    { key: 'guide', label: 'Guide', icon: <Inbox size={16} />, count: data.counts.guide_leads, rows: data.guide, cols: ['first_name', 'email', 'received_at'] },
  ];
  const active = TABS.find(t => t.key === tab) || TABS[0];

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <header className="sticky top-0 z-10 bg-[var(--bg)] border-b border-[var(--line)]">
        <div className="k-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/brand/kindra-logo-light.svg" alt="Kindra" className="h-6" />
            <Badge tone="neutral">Admin</Badge>
          </div>
          <Button variant="secondary" size="sm" onClick={logout} data-testid="admin-logout"><LogOut size={14} /> Sign out</Button>
        </div>
      </header>

      <div className="k-container py-8">
        <CanaryPanel />
        <QrFunnelTile />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="admin-stat-cards">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`text-left rounded-md p-4 border transition-colors ${tab === t.key ? 'border-[var(--ink)] bg-[var(--tint)]' : 'border-[var(--line)] bg-white hover:bg-[var(--tint)]'}`} data-testid={`admin-tab-${t.key}`}>
              <div className="flex items-center gap-2 k-eyebrow text-[var(--muted)]">{t.icon} {t.label}</div>
              <div className="font-display font-bold text-[28px] text-[var(--ink)] mt-1 tabular-nums">{t.count}</div>
            </button>
          ))}
        </div>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-[14px]" data-testid={`admin-table-${active.key}`}>
              <thead className="bg-[var(--tint)] text-[var(--muted)]">
                <tr>{active.cols.map(c => <th key={c} className="px-4 py-2 k-small font-semibold uppercase tracking-wide">{c.replace(/_/g, ' ')}</th>)}</tr>
              </thead>
              <tbody>
                {active.rows.length === 0 && (
                  <tr><td colSpan={active.cols.length} className="px-4 py-10 text-center k-body text-[var(--muted)]">No {active.label.toLowerCase()} yet.</td></tr>
                )}
                {active.rows.map((r, i) => (
                  <tr key={i} className="border-t border-[var(--line)]">
                    {active.cols.map(c => <td key={c} className="px-4 py-3 text-[var(--ink)] tabular-nums align-top max-w-[24ch] truncate" title={String(r[c] ?? '')}>{String(r[c] ?? '—')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <p className="k-small text-[var(--muted)] mt-6">Showing most recent 100 rows per type. Drill-down + filters land in V1.1.</p>
      </div>
    </div>
  );
};


// ============================================================================
// Money Path Canary — surfaces the latest /api/admin/launch-gate run and
// lets the admin trigger a fresh one manually. The cron entry on the host
// runs this every night via /api/admin/launch-gate/cron (setup-token auth).
// ============================================================================
const CanaryPanel = () => {
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/launch-gate/history?limit=5`, auth());
      setHistory(r.data?.results || []);
    } catch (_) { /* silently swallow */ }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true); setErr('');
    try {
      await axios.post(`${API}/admin/launch-gate/run`, {}, { ...auth(), timeout: 180000 });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Canary failed to start');
    } finally { setRunning(false); }
  };

  const latest = history[0];
  const stateLabel = latest ? (latest.overall === 'pass' ? 'PASS' : 'FAIL') : 'NEVER RUN';
  const stateTone = latest?.overall === 'pass' ? 'low' : (latest ? 'high' : 'neutral');

  return (
    <Card className="p-5 mb-6" data-testid="admin-canary-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Activity size={18} className="text-[var(--muted)]" />
          <div>
            <p className="k-eyebrow text-[var(--muted)]">Money Path Canary</p>
            <p className="k-h3 text-[var(--ink)] mt-1">
              {latest ? (
                <>
                  Last run: <Badge tone={stateTone}>{stateLabel}</Badge>{' '}
                  <span className="k-small text-[var(--muted)] ml-1">
                    {new Date(latest.finished_at).toLocaleString()} · {latest.run_id}
                  </span>
                </>
              ) : (
                <>No runs yet — kick it off to verify the money path end-to-end.</>
              )}
            </p>
          </div>
        </div>
        <Button variant="primary" size="md" onClick={run} disabled={running} data-testid="admin-canary-run">
          {running ? 'Running…' : 'Run canary now'}
        </Button>
      </div>

      {err && <p className="k-small text-[#B82344] mt-3">{err}</p>}

      {latest && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-canary-steps">
          {Object.entries(latest.steps || {}).map(([step, val]) => (
            <div
              key={step}
              className="rounded-md border border-[var(--line)] px-3 py-2 bg-[var(--surface)]"
              style={{ borderLeftWidth: 3, borderLeftColor: val.ok ? '#34D1B0' : '#EB258D' }}
              title={val.error || val.note || ''}
            >
              <p className="k-eyebrow text-[var(--muted)]">{step.replace(/_/g, ' ')}</p>
              <p className="k-small text-[var(--ink)] font-semibold mt-1">{val.ok ? 'pass' : 'fail'}</p>
            </div>
          ))}
        </div>
      )}

      {history.length > 1 && (
        <details className="mt-4">
          <summary className="k-small text-[var(--muted)] cursor-pointer">History (last {history.length})</summary>
          <ul className="mt-2 space-y-1">
            {history.map(h => (
              <li key={h.id} className="k-small text-[var(--muted)] tabular-nums">
                {h.overall === 'pass' ? '✓' : '✗'} {new Date(h.finished_at).toLocaleString()} · {h.run_id} {h.failed_steps?.length ? `(failed: ${h.failed_steps.join(', ')})` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
};


// ============================================================================
// QR Funnel tile — first-party "is the carpark sticker working?" metric.
// Counts views (unique sessions from /clarity-view beacon) → calculator
// captures → bookings → paid. Scoped to any kindra_source carrying the
// `qr-` prefix so future campaigns (qr-clarity, qr-event-2026, etc.) roll up.
// ============================================================================
const QrFunnelTile = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [range, setRange] = useState('all');

  const load = async (r = range) => {
    try {
      const res = await axios.get(`${API}/admin/qr-funnel?range=${r}`, auth());
      setData(res.data || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load QR funnel');
    }
  };
  useEffect(() => { load(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  const RANGES = [
    { key: '1d',  label: 'Today' },
    { key: '7d',  label: 'Last 7' },
    { key: '30d', label: 'Last 30' },
    { key: 'all', label: 'All time' },
  ];

  if (err) return null;
  if (!data) {
    return (
      <Card className="p-5 mb-6" data-testid="admin-qr-funnel">
        <p className="k-eyebrow text-[var(--muted)]">QR funnel</p>
        <p className="k-small text-[var(--muted)] mt-2">Loading…</p>
      </Card>
    );
  }

  const stages = [
    { label: 'Views',              value: data.views_total ?? 0, sub: 'unique sessions' },
    { label: 'Calculator captures', value: data.calc_captures ?? 0, sub: 'contacts tagged qr-' },
    { label: 'Bookings',           value: data.bookings ?? 0, sub: 'deals tagged qr-' },
    { label: 'Paid',               value: data.paid ?? 0, sub: 'deals at Audit Sold' },
  ];

  const conv = (a, b) => (b > 0 ? `${((a / b) * 100).toFixed(0)}%` : '—');

  return (
    <Card className="p-5 mb-6" data-testid="admin-qr-funnel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="k-eyebrow text-[var(--muted)]">QR funnel</p>
          <p className="k-h3 text-[var(--ink)] mt-1">Is the carpark sticker working?</p>
        </div>
        <div className="flex items-center gap-1 rounded-pill bg-[var(--surface)] border border-[var(--line)] p-1" data-testid="qr-funnel-range">
          {RANGES.map(r => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              data-testid={`qr-funnel-range-${r.key}`}
              data-active={range === r.key}
              className={`px-3 py-1 rounded-pill text-sm font-semibold transition ${range === r.key ? 'text-[var(--ink)]' : 'text-[var(--muted)] hover:text-[var(--ink)]'}`}
              style={range === r.key ? { background: 'var(--spectrum)', color: 'var(--icy)' } : undefined}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-qr-funnel-stages">
        {stages.map((s, i) => (
          <div key={s.label} className="rounded-md border border-[var(--line)] p-4 bg-[var(--surface)]"
               style={{ borderLeftWidth: 3, borderLeftColor: '#9156C4' }}>
            <p className="k-eyebrow text-[var(--muted)]">{s.label}</p>
            <p className="k-numeric-xl text-[var(--ink)] tabular-nums mt-1">{s.value}</p>
            <p className="k-small text-[var(--muted)] mt-1">{s.sub}</p>
            {i > 0 && (
              <p className="k-small text-[var(--muted)] mt-2">
                Conv from {stages[i - 1].label.toLowerCase()}: <strong className="text-[var(--ink)]">{conv(s.value, stages[i - 1].value)}</strong>
              </p>
            )}
          </div>
        ))}
      </div>

      {data.views_by_source && Object.keys(data.views_by_source).length > 1 && (
        <div className="mt-4 k-small text-[var(--muted)]">
          By source: {Object.entries(data.views_by_source).map(([k, v]) => `${k}=${v}`).join(' · ')}
        </div>
      )}
    </Card>
  );
};

